import { useEffect, useMemo, useRef, useState } from 'react';
import { Autoplay, EffectFade, Keyboard } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { assetUrl } from '../../../services/storefrontService.js';

import 'swiper/css';
import 'swiper/css/effect-fade';

const AUTOPLAY_DELAY = 5000;

function cleanText(value) {
  return String(value || '').trim();
}

function normalizeUrl(url, baseUrl = '') {
  const value = cleanText(url);
  if (!value) return '#';
  if (value.startsWith('http') || value.startsWith('mailto:') || value.startsWith('tel:') || value.startsWith('#')) {
    return value;
  }

  const cleanUrl = value.startsWith('/') ? value : `/${value}`;
  if (baseUrl && !cleanUrl.startsWith(baseUrl)) {
    return `${baseUrl}${cleanUrl}`;
  }

  return cleanUrl;
}

function normalizeButtons(slide) {
  if (Array.isArray(slide.buttons) && slide.buttons.length > 0) {
    return slide.buttons
      .map((button) => ({
        label: cleanText(button.label || button.text || button.ctaText || button.cta_text),
        url: button.url || button.href || button.ctaUrl || button.cta_url || '#',
      }))
      .filter((button) => button.label);
  }

  const label = cleanText(slide.buttonText || slide.ctaText || slide.cta_text || slide.button_text);
  const url = slide.buttonUrl || slide.ctaUrl || slide.cta_url || slide.button_url || '#';

  return label ? [{ label, url }] : [];
}

function imageUrl(path) {
  return path ? assetUrl(path) : '';
}

function normalizeSlides(slides) {
  return (slides || [])
    .map((slide, index) => {
      const desktop = slide.image_desktop || slide.imageDesktop || slide.image || slide.photo || '';
      const mobile = slide.image_mobile || slide.imageMobile || slide.image_desktop || slide.imageDesktop || slide.image || slide.photo || '';

      return {
        id: slide.id || `carousel-2-${index}`,
        title: cleanText(slide.title || slide.title_en || slide.heading),
        subtitle: cleanText(slide.subtitle || slide.subtitle_en || slide.content),
        eyebrowLogo: imageUrl(slide.eyebrowLogo || slide.logo || slide.logo_url || slide.brand_logo),
        eyebrowText: cleanText(slide.eyebrowText || slide.eyebrow_text || slide.eyebrow),
        imageDesktop: imageUrl(desktop),
        imageMobile: imageUrl(mobile),
        alt: cleanText(slide.alt || slide.title || slide.title_en || slide.heading || 'La Bella Trading'),
        overlayOpacity: Number(slide.overlay_opacity ?? slide.overlayOpacity ?? 0.3),
        overlayStyle: slide.overlay_style || slide.overlayStyle || 'gradient',
        buttons: normalizeButtons(slide),
      };
    })
    .filter((slide) => slide.imageDesktop || slide.imageMobile);
}

function ProgressBullet({ active, paused, index, onClick }) {
  return (
    <button
      type="button"
      className={`carousel2-bullet${active ? ' is-active' : ''}${paused ? ' is-paused' : ''}`}
      onClick={onClick}
      aria-label={`Go to slide ${index + 1}`}
      aria-current={active ? 'true' : undefined}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <circle className="carousel2-progress-bg" cx="6" cy="6" r="5" fill="none" />
        <circle key={`${index}-${active}-${paused}`} className="carousel2-progress-bar" cx="6" cy="6" r="5" fill="none" />
      </svg>
    </button>
  );
}

function PlayPauseIcon({ paused }) {
  if (paused) {
    return (
      <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
        <path d="M1.5.8v6.4L7 4 1.5.8Z" fill="currentColor" />
      </svg>
    );
  }

  return (
    <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
      <path d="M1 0h2v8H1V0Zm4 0h2v8H5V0Z" fill="currentColor" />
    </svg>
  );
}

export default function HeroCarousel({ slides = [], baseUrl = '', fontFamily }) {
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const items = useMemo(() => normalizeSlides(slides), [slides]);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper?.autoplay) return;

    if (paused) {
      swiper.autoplay.stop();
    } else {
      swiper.autoplay.start();
    }
  }, [paused]);

  if (!items.length) return null;

  const heroFont = fontFamily || "'Inter', sans-serif";

  return (
    <section className="carousel2-section" style={{ fontFamily: heroFont }}>
      <style>{`
        .carousel2-section {
          --carousel2-delay: ${AUTOPLAY_DELAY}ms;
          position: relative;
          width: 100%;
          height: calc(80svh - var(--announcement-bar-height, 0px));
          min-height: 520px;
          max-height: 860px;
          overflow: hidden;
          margin: 0 !important;
          padding: 0 !important;
          background: #fff;
          color: #fff;
          isolation: isolate;
          line-height: normal;
        }

        .carousel2-section > .swiper,
        .carousel2-section .swiper-wrapper,
        .carousel2-section .swiper-slide {
          width: 100%;
          height: 100%;
        }

        .carousel2-section .swiper-slide {
          pointer-events: none;
        }

        .carousel2-section .swiper-slide-active {
          pointer-events: auto;
        }

        .carousel2-section > .swiper {
          position: absolute;
          inset: 0;
        }

        .carousel2-slide {
          position: relative;
          inset: auto;
          width: 100%;
          height: 100%;
          overflow: hidden;
          opacity: 1;
          pointer-events: auto;
          transition: none;
        }

        .carousel2-picture,
        .carousel2-image {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          display: block;
        }

        .carousel2-image {
          object-fit: cover;
          object-position: center;
          transform: none;
          transition: transform 900ms ease;
        }

        .swiper-slide-active .carousel2-image {
          transform: none;
        }

        .carousel2-overlay {
          position: absolute;
          inset: 0;
          z-index: 2;
          pointer-events: none;
        }

        .carousel2-overlay.is-gradient {
          background:
            radial-gradient(circle at 0% 0%, rgba(0, 0, 0, 0.18) 0%, rgba(0, 0, 0, 0) 34%),
            radial-gradient(circle at 0% 100%, rgba(0, 0, 0, 0.34) 0%, rgba(0, 0, 0, 0) 42%),
            linear-gradient(180deg, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0) 20%, rgba(0, 0, 0, 0) 70%, rgba(0, 0, 0, 0.26) 100%);
        }

        .carousel2-overlay.is-solid {
          background:
            radial-gradient(circle at 0% 0%, rgba(0, 0, 0, 0.16) 0%, rgba(0, 0, 0, 0) 34%),
            radial-gradient(circle at 0% 100%, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 42%),
            linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0) 20%, rgba(0, 0, 0, 0) 70%, rgba(0, 0, 0, 0.22) 100%);
          opacity: 1;
        }

        .carousel2-eyebrow-bar {
          position: absolute;
          top: clamp(30px, 4.6vh, 40px);
          left: 0;
          right: 0;
          z-index: 5;
          pointer-events: none;
        }

        .carousel2-logo {
          display: block;
          max-width: min(220px, 42vw);
          max-height: 24px;
          object-fit: contain;
        }

        .carousel2-eyebrow-text {
          color: rgba(255, 255, 255, 0.78);
          font-family: Georgia, 'Times New Roman', serif;
          font-size: 25px;
          font-style: italic;
          font-weight: 700;
          line-height: 1.05;
          letter-spacing: 0;
          text-transform: uppercase;
          pointer-events: none;
        }

        .carousel2-content {
          position: absolute;
          inset: 0;
          z-index: 4;
          display: flex;
          align-items: flex-end;
          padding-top: clamp(90px, 14vh, 140px);
          padding-bottom: clamp(30px, 5vh, 44px);
          pointer-events: none;
        }

        .carousel2-content .container,
        .carousel2-eyebrow-bar .container {
          width: min(1220px, calc(100% - 40px));
          margin: 0 auto;
          pointer-events: none;
        }

        .carousel2-copy {
          width: min(520px, 92vw);
          opacity: 0;
          transform: translateY(16px);
          animation: carousel2FadeUp 560ms ease forwards;
        }

        .carousel2-copy-controls {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
          color: #fff;
          pointer-events: auto;
        }

        .carousel2-title {
          margin: 0;
          color: #fff;
          font-size: 24px;
          font-weight: 600;
          line-height: 1.08;
          letter-spacing: 0;
          text-transform: none;
          text-wrap: balance;
        }

        .carousel2-subtitle {
          margin-top: 5px;
          color: rgba(255, 255, 255, 0.95);
          font-size: 14px;
          font-weight: 400;
          line-height: 1.25;
          letter-spacing: 0;
          text-transform: none;
        }

        .carousel2-actions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 10px;
          margin-top: 22px;
          pointer-events: auto;
        }

        .carousel2-cta {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 34px;
          padding: 10px 14px;
          overflow: hidden;
          border-radius: 999px;
          color: #fff;
          font-size: 11px;
          font-weight: 600;
          line-height: 1;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          text-decoration: none;
          isolation: isolate;
          transition: color 180ms ease;
        }

        .carousel2-cta::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: -1;
          border-radius: inherit;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          transition: background 180ms ease;
        }

        .carousel2-cta:hover::before,
        .carousel2-cta:focus-visible::before {
          background: rgba(255, 255, 255, 0.6);
        }

        .carousel2-cta:focus-visible,
        .carousel2-bullet:focus-visible,
        .carousel2-play-toggle:focus-visible {
          outline: 1px solid rgba(255, 255, 255, 0.9);
          outline-offset: 3px;
        }

        .carousel2-pagination,
        .carousel2-bullets {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 0;
          color: #fff;
          pointer-events: auto;
        }

        .carousel2-bullet,
        .carousel2-play-toggle {
          appearance: none;
          border: 0;
          padding: 0;
          margin: 0;
          background: transparent;
          color: #fff;
          cursor: pointer;
        }

        .carousel2-bullet {
          display: inline-flex;
          width: 12px;
          height: 12px;
        }

        .carousel2-progress-bg {
          stroke: rgba(255, 255, 255, 0.4);
          stroke-width: 1.3;
        }

        .carousel2-progress-bar {
          stroke: #fff;
          stroke-width: 1.3;
          stroke-linecap: round;
          stroke-dasharray: 31.4;
          stroke-dashoffset: 31.4;
          transform: rotate(-90deg);
          transform-origin: 50% 50%;
        }

        .carousel2-bullet.is-active .carousel2-progress-bar {
          animation: carousel2Progress var(--carousel2-delay) linear forwards;
          animation-play-state: running;
        }

        .carousel2-bullet.is-active.is-paused .carousel2-progress-bar {
          animation-play-state: paused;
        }

        .carousel2-divider {
          width: 1px;
          height: 16px;
          background: rgba(255, 255, 255, 0.24);
        }

        .carousel2-play-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
        }

        .carousel2-section .swiper-slide:not(.swiper-slide-active) .carousel2-copy-controls,
        .carousel2-section .swiper-slide:not(.swiper-slide-active) .carousel2-pagination,
        .carousel2-section .swiper-slide:not(.swiper-slide-active) .carousel2-actions,
        .carousel2-section .swiper-slide:not(.swiper-slide-active) .carousel2-cta,
        .carousel2-section .swiper-slide:not(.swiper-slide-active) .carousel2-bullet,
        .carousel2-section .swiper-slide:not(.swiper-slide-active) .carousel2-play-toggle {
          pointer-events: none !important;
        }

        @keyframes carousel2Progress {
          from { stroke-dashoffset: 31.4; }
          to { stroke-dashoffset: 0; }
        }

        @keyframes carousel2FadeUp {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 767px) {
          .carousel2-section {
            height: calc(80svh - var(--announcement-bar-height, 0px));
            min-height: 560px;
          }

          .carousel2-content {
            padding-bottom: 34px;
          }

          .carousel2-content .container,
          .carousel2-eyebrow-bar .container {
            width: calc(100% - 28px);
          }

          .carousel2-title {
            font-size: 21px;
            font-weight: 600;
          }

          .carousel2-subtitle {
            font-size: 13px;
            font-weight: 400;
          }

          .carousel2-eyebrow-text {
            font-size: 22px;
          }

          .carousel2-actions {
            gap: 8px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .carousel2-image,
          .carousel2-cta::before {
            transition: none;
          }

          .carousel2-copy,
          .carousel2-bullet.is-active .carousel2-progress-bar {
            animation: none;
          }
        }
      `}</style>

      <Swiper
        modules={[Autoplay, EffectFade, Keyboard]}
        effect="fade"
        speed={650}
        loop={items.length > 1}
        keyboard={{ enabled: true }}
        autoplay={items.length > 1 ? { delay: AUTOPLAY_DELAY, disableOnInteraction: false } : false}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex);
        }}
      >
        {items.map((slide, index) => (
          <SwiperSlide key={slide.id}>
            <div className="carousel2-slide">
              <picture className="carousel2-picture">
                {slide.imageMobile ? <source media="(max-width: 767px)" srcSet={slide.imageMobile} /> : null}
                <img
                  src={slide.imageDesktop || slide.imageMobile}
                  alt={slide.alt}
                  className="carousel2-image"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  fetchPriority={index === 0 ? 'high' : 'auto'}
                />
              </picture>

              <div
                className={`carousel2-overlay is-${slide.overlayStyle === 'solid' ? 'solid' : 'gradient'}`}
                style={{ '--carousel2-overlay-opacity': slide.overlayOpacity }}
              />

              {(slide.eyebrowLogo || slide.eyebrowText) ? (
                <div className="carousel2-eyebrow-bar">
                  <div className="container">
                    {slide.eyebrowLogo ? (
                      <img src={slide.eyebrowLogo} alt="" className="carousel2-logo" loading={index === 0 ? 'eager' : 'lazy'} />
                    ) : (
                      <div className="carousel2-eyebrow-text">{slide.eyebrowText}</div>
                    )}
                  </div>
                </div>
              ) : null}

              <div className="carousel2-content">
                <div className="container">
                  <div className="carousel2-copy" key={`${slide.id}-${activeIndex}`}>
                    {items.length > 1 ? (
                      <div className="carousel2-copy-controls" aria-label="Hero carousel controls">
                        <div className="carousel2-pagination">
                          <div className="carousel2-bullets">
                            {items.map((bulletSlide, bulletIndex) => (
                              <ProgressBullet
                                key={`carousel2-bullet-${bulletSlide.id}`}
                                active={bulletIndex === activeIndex}
                                paused={paused}
                                index={bulletIndex}
                                onClick={() => swiperRef.current?.slideToLoop(bulletIndex)}
                              />
                            ))}
                          </div>
                          <div className="carousel2-divider" aria-hidden="true" />
                          <button
                            type="button"
                            className="carousel2-play-toggle"
                            onClick={() => setPaused((value) => !value)}
                            aria-label={paused ? 'Play carousel' : 'Pause carousel'}
                          >
                            <PlayPauseIcon paused={paused} />
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {slide.title ? <h2 className="carousel2-title">{slide.title}</h2> : null}
                    {slide.subtitle ? <div className="carousel2-subtitle">{slide.subtitle}</div> : null}

                    {slide.buttons.length > 0 ? (
                      <div className="carousel2-actions">
                        {slide.buttons.map((button, buttonIndex) => (
                          <a key={`${slide.id}-button-${buttonIndex}`} className="carousel2-cta" href={normalizeUrl(button.url, baseUrl)}>
                            {button.label}
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
}
